/**
 * PrintService - Map canvas export and PDF/image generation
 *
 * Provides functionality for:
 * - Capturing the OpenLayers map canvas
 * - Generating PDF documents with jsPDF
 * - Exporting map as PNG, JPG, or SVG images
 * - Adding cartographic elements (title, legend, scale bar, north arrow)
 *
 * **Validates: Requirements 36, 37**
 */

import { jsPDF } from 'jspdf';
import type Map from 'ol/Map';
import type {
  PrintOptions,
  ImageExportOptions,
  PageDimensions,
  PageSize,
  Orientation,
  DPI,
  MapLayout,
  LayoutElement,
} from '../types/print';
import { PAGE_DIMENSIONS } from '../types/print';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MM_TO_INCHES = 1 / 25.4;
const MARGIN_MM = 10;

export interface SimpleReportContent {
  notes?: string;
  measurements?: string[];
}

// ---------------------------------------------------------------------------
// Canvas capture
// ---------------------------------------------------------------------------

/**
 * Place an image inside a box while preserving aspect ratio (contain + center).
 * Prevents the classic “shrunk / stretched map” look in PDF export.
 */
export function fitImageInBox(
  imgW: number,
  imgH: number,
  boxX: number,
  boxY: number,
  boxW: number,
  boxH: number
): { x: number; y: number; w: number; h: number } {
  if (imgW <= 0 || imgH <= 0 || boxW <= 0 || boxH <= 0) {
    return { x: boxX, y: boxY, w: Math.max(0, boxW), h: Math.max(0, boxH) };
  }
  const imgAspect = imgW / imgH;
  const boxAspect = boxW / boxH;
  let w: number;
  let h: number;
  if (imgAspect > boxAspect) {
    w = boxW;
    h = boxW / imgAspect;
  } else {
    h = boxH;
    w = boxH * imgAspect;
  }
  return {
    x: boxX + (boxW - w) / 2,
    y: boxY + (boxH - h) / 2,
    w,
    h,
  };
}

/**
 * Captures the current map as an HTMLCanvasElement.
 * Uses OpenLayers renderSync() and the official layer-transform composite path
 * so HiDPI / retina canvases are not drawn at the wrong scale (shrunk or cropped).
 *
 * @param map - The OpenLayers Map instance
 * @returns A canvas element containing the rendered map (CSS pixel size)
 */
export function captureMapCanvas(map: Map): HTMLCanvasElement {
  // Ensure layout size is current (sidebar / wizard can leave stale sizes)
  try {
    map.updateSize();
  } catch {
    /* optional on mocks */
  }
  map.renderSync();

  const size = map.getSize();
  if (!size || size[0] <= 0 || size[1] <= 0) {
    throw new Error('Map has no size. Ensure the map is rendered before capturing.');
  }

  const width = Math.floor(size[0]);
  const height = Math.floor(size[1]);

  const mapCanvas = document.createElement('canvas');
  mapCanvas.width = width;
  mapCanvas.height = height;
  const mapContext = mapCanvas.getContext('2d');
  if (!mapContext) {
    throw new Error('Failed to get 2D context from canvas.');
  }

  // Opaque background — PDF readers treat transparent PNG as black/odd
  mapContext.fillStyle = '#ffffff';
  mapContext.fillRect(0, 0, width, height);

  const mapViewport = map.getViewport();
  // Prefer OL layer canvases; fall back to any canvas in the viewport
  let canvases = mapViewport.querySelectorAll<HTMLCanvasElement>(
    '.ol-layer canvas, canvas.ol-layer'
  );
  if (canvases.length === 0) {
    canvases = mapViewport.querySelectorAll('canvas');
  }

  canvases.forEach((canvas) => {
    if (canvas.width <= 0 || canvas.height <= 0) return;

    const parent = canvas.parentElement as HTMLElement | null;
    const opacity = parent?.style?.opacity;
    mapContext.globalAlpha =
      opacity === '' || opacity == null || Number.isNaN(Number(opacity)) ? 1 : Number(opacity);

    const transform = canvas.style.transform || '';
    const matrixMatch = transform.match(/matrix\(([^)]+)\)/);

    if (matrixMatch) {
      // OpenLayers HiDPI path: style transform scales bitmap → CSS pixels
      const values = matrixMatch[1].split(',').map((v) => Number(String(v).trim()));
      if (values.length === 6 && values.every((n) => Number.isFinite(n))) {
        mapContext.setTransform(values[0], values[1], values[2], values[3], values[4], values[5]);
        mapContext.drawImage(canvas, 0, 0);
      } else {
        mapContext.setTransform(1, 0, 0, 1, 0, 0);
        mapContext.drawImage(canvas, 0, 0, width, height);
      }
    } else {
      // Fallback: draw at CSS size (not raw bitmap size) so retina doesn't "zoom"
      mapContext.setTransform(1, 0, 0, 1, 0, 0);
      const cssW = parseFloat(canvas.style.width) || width;
      const cssH = parseFloat(canvas.style.height) || height;
      const translateMatch = transform.match(/translate3?d?\(\s*(-?[\d.]+)px\s*,\s*(-?[\d.]+)px/);
      const tx = translateMatch ? parseFloat(translateMatch[1]) : 0;
      const ty = translateMatch ? parseFloat(translateMatch[2]) : 0;
      mapContext.drawImage(canvas, tx, ty, cssW, cssH);
    }
  });

  mapContext.globalAlpha = 1;
  mapContext.setTransform(1, 0, 0, 1, 0, 0);

  return mapCanvas;
}

/**
 * Captures the map canvas at a specific DPI by scaling.
 *
 * @param map - The OpenLayers Map instance
 * @param dpi - Target DPI (72, 150, or 300)
 * @returns A scaled canvas element
 */
export function captureMapCanvasAtDPI(map: Map, dpi: DPI): HTMLCanvasElement {
  const baseCanvas = captureMapCanvas(map);
  const scaleFactor = dpi / 72; // 72 is screen DPI

  if (scaleFactor === 1) {
    return baseCanvas;
  }

  const scaledCanvas = document.createElement('canvas');
  scaledCanvas.width = baseCanvas.width * scaleFactor;
  scaledCanvas.height = baseCanvas.height * scaleFactor;
  const ctx = scaledCanvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get 2D context for scaled canvas.');
  }

  ctx.scale(scaleFactor, scaleFactor);
  ctx.drawImage(baseCanvas, 0, 0);

  return scaledCanvas;
}

// ---------------------------------------------------------------------------
// Page dimension helpers
// ---------------------------------------------------------------------------

/**
 * Gets the effective page dimensions based on page size and orientation.
 */
export function getPageDimensions(
  pageSize: PageSize,
  orientation: Orientation,
  customWidth?: number,
  customHeight?: number
): PageDimensions {
  let dims: PageDimensions;

  if (pageSize === 'Custom') {
    dims = {
      width: customWidth ?? 210,
      height: customHeight ?? 297,
    };
  } else {
    dims = { ...PAGE_DIMENSIONS[pageSize] };
  }

  // Swap for landscape
  if (orientation === 'landscape') {
    return { width: dims.height, height: dims.width };
  }

  return dims;
}

// ---------------------------------------------------------------------------
// PDF generation
// ---------------------------------------------------------------------------

/**
 * Generates a PDF document from the map canvas with optional cartographic elements.
 *
 * @param map - The OpenLayers Map instance
 * @param options - Print configuration options
 * @param layers - Array of layer info for legend generation
 * @returns A Blob containing the PDF
 *
 * **Validates: Requirements 36.3, 36.4, 36.5, 36.6**
 */
export function generatePDF(
  map: Map | null,
  options: PrintOptions,
  layers?: Array<{ name: string; color: string; geometryType: string }>,
  reportContent?: SimpleReportContent
): Blob {
  const dims = getPageDimensions(
    options.pageSize,
    options.orientation,
    options.customWidth,
    options.customHeight
  );

  const pdf = new jsPDF({
    orientation: options.orientation === 'landscape' ? 'landscape' : 'portrait',
    unit: 'mm',
    format:
      options.pageSize === 'Custom'
        ? [dims.width, dims.height]
        : options.pageSize.toLowerCase() === 'letter'
          ? 'letter'
          : options.pageSize.toLowerCase(),
  });

  const pageWidth = dims.width;
  const pageHeight = dims.height;
  const hasReportContent = Boolean(
    reportContent?.notes?.trim() || reportContent?.measurements?.length
  );

  let currentY = MARGIN_MM;

  if (options.includeTitle && options.title) {
    currentY = addTitleToPDF(pdf, options.title, pageWidth, currentY);
  }

  if (!map) {
    if (reportContent) {
      addSimpleReportContent(pdf, reportContent, pageWidth, pageHeight, currentY);
    }
    return pdf.output('blob');
  }

  // Reserved layout boxes (mm)
  const hasLegend = !!(options.includeLegend && layers && layers.length > 0);
  const legendColW = hasLegend ? Math.min(48, pageWidth * 0.22) : 0;
  const scaleReserve = options.includeScaleBar ? 16 : 0;

  const boxX = MARGIN_MM;
  const boxY = currentY;
  const boxW = pageWidth - 2 * MARGIN_MM - (hasLegend ? legendColW + 4 : 0);
  const boxH = pageHeight - currentY - MARGIN_MM - scaleReserve;

  // Capture map at correct CSS scale (HiDPI-safe)
  const mapCanvas = captureMapCanvas(map);
  const mapDataUrl = mapCanvas.toDataURL('image/png');

  // Fit map into box WITHOUT stretching (fixes “shrinking / squashed” map)
  const fitted = fitImageInBox(mapCanvas.width, mapCanvas.height, boxX, boxY, boxW, boxH);

  // Light frame so letterboxed area still looks intentional
  pdf.setDrawColor(200, 200, 200);
  pdf.setFillColor(248, 250, 252);
  pdf.setLineWidth(0.2);
  pdf.rect(boxX, boxY, boxW, boxH, 'FD');

  pdf.addImage(mapDataUrl, 'PNG', fitted.x, fitted.y, fitted.w, fitted.h, undefined, 'FAST');

  // North arrow on the map frame (top-right of fitted image)
  if (options.includeNorthArrow) {
    addNorthArrowToPDF(pdf, fitted.x + fitted.w - 15, fitted.y + 5);
  }

  // Legend in dedicated column (not overlapping the map)
  if (hasLegend && layers) {
    const legendX = boxX + boxW + 4;
    addLegendToPDF(pdf, layers, legendX, boxY, legendColW - 2);
  }

  // Scale bar under map box, aligned to fitted map width when possible
  if (options.includeScaleBar) {
    const scaleBarY = boxY + boxH + 4;
    addScaleBarToPDF(pdf, map, fitted.x, scaleBarY, fitted.w);
  }

  if (hasReportContent && reportContent) {
    pdf.addPage();
    let reportY = MARGIN_MM;
    if (options.includeTitle && options.title) {
      reportY = addTitleToPDF(pdf, options.title, pageWidth, reportY);
    }
    addSimpleReportContent(pdf, reportContent, pageWidth, pageHeight, reportY);
  }

  return pdf.output('blob');
}

/**
 * Print Composer v2 — Automated Plan + Profile sheet
 * Generates a professional multi-element sheet with plan view + longitudinal profile + labels.
 */
export function generatePlanProfilePDF(
  map: Map,
  options: PrintOptions,
  profileData: Array<{ station: number; elevation: number }>,
  alignmentInfo?: { name: string; length: number; avgGrade?: number }
): Blob {
  const dims = getPageDimensions(options.pageSize, options.orientation);
  const pdf = new jsPDF({
    orientation: options.orientation === 'landscape' ? 'landscape' : 'portrait',
    unit: 'mm',
    format:
      options.pageSize === 'Custom' ? [dims.width, dims.height] : options.pageSize.toLowerCase(),
  });

  const pageWidth = dims.width;
  const pageHeight = dims.height;
  let y = 15;

  // Title block
  pdf.setFontSize(14);
  pdf.text(`PLAN + PROFILE — ${alignmentInfo?.name || 'Alignment'}`, 15, y);
  y += 8;
  pdf.setFontSize(10);
  pdf.text(
    `Length: ${alignmentInfo?.length?.toFixed(1) || '?'} m    Generated: ${new Date().toLocaleDateString()}`,
    15,
    y
  );
  y += 10;

  // Plan view (top half) — preserve map aspect ratio
  const planBoxW = pageWidth - 30;
  const planBoxH = (pageHeight - y - 20) * 0.55;
  const mapCanvas = captureMapCanvas(map);
  const mapData = mapCanvas.toDataURL('image/png');
  const planFit = fitImageInBox(mapCanvas.width, mapCanvas.height, 15, y, planBoxW, planBoxH);
  pdf.setDrawColor(200, 200, 200);
  pdf.setFillColor(248, 250, 252);
  pdf.rect(15, y, planBoxW, planBoxH, 'FD');
  pdf.addImage(mapData, 'PNG', planFit.x, planFit.y, planFit.w, planFit.h, undefined, 'FAST');
  y += planBoxH + 8;

  // Profile graph (bottom)
  pdf.setFontSize(11);
  pdf.text('LONGITUDINAL PROFILE', 15, y);
  y += 5;

  if (profileData.length > 1) {
    const minE = Math.min(...profileData.map((p) => p.elevation));
    const maxE = Math.max(...profileData.map((p) => p.elevation));
    const minS = profileData[0].station;
    const maxS = profileData[profileData.length - 1].station;

    const graphX = 15;
    const graphY = y;
    const graphW = pageWidth - 30;
    const graphH = 55;

    pdf.rect(graphX, graphY, graphW, graphH);

    pdf.setDrawColor(34, 197, 94);
    pdf.setLineWidth(0.8);
    for (let i = 0; i < profileData.length - 1; i++) {
      const p1 = profileData[i];
      const p2 = profileData[i + 1];
      const x1 = graphX + ((p1.station - minS) / (maxS - minS || 1)) * graphW;
      const y1 = graphY + graphH - ((p1.elevation - minE) / (maxE - minE || 1)) * graphH;
      const x2 = graphX + ((p2.station - minS) / (maxS - minS || 1)) * graphW;
      const y2 = graphY + graphH - ((p2.elevation - minE) / (maxE - minE || 1)) * graphH;
      pdf.line(x1, y1, x2, y2);
    }

    pdf.setFontSize(7);
    for (let i = 0; i <= 4; i++) {
      const s = minS + (i / 4) * (maxS - minS);
      const lx = graphX + (i / 4) * graphW;
      pdf.text(s.toFixed(0) + 'm', lx - 5, graphY + graphH + 4);
    }
  }

  y += 65;

  if (alignmentInfo) {
    pdf.setFontSize(9);
    pdf.text(
      `Alignment: ${alignmentInfo.name}   Total Length: ${alignmentInfo.length.toFixed(1)}m`,
      15,
      y
    );
  }

  return pdf.output('blob');
}

/**
 * Adds a title to the PDF document.
 */
function addTitleToPDF(pdf: jsPDF, title: string, pageWidth: number, y: number): number {
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text(title, pageWidth / 2, y + 6, { align: 'center' });
  return y + 14;
}

function addSimpleReportContent(
  pdf: jsPDF,
  content: SimpleReportContent,
  pageWidth: number,
  pageHeight: number,
  startY: number
): void {
  const maxWidth = pageWidth - 2 * MARGIN_MM;
  const bottomY = pageHeight - MARGIN_MM;
  let y = startY;

  const ensureSpace = (height: number) => {
    if (y + height > bottomY) {
      pdf.addPage();
      y = MARGIN_MM;
    }
  };

  const addHeading = (heading: string) => {
    ensureSpace(9);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.text(heading, MARGIN_MM, y + 4);
    y += 9;
  };

  const addWrappedText = (text: string) => {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    const lines = pdf.splitTextToSize(text, maxWidth) as string[];
    for (const line of lines) {
      ensureSpace(6);
      pdf.text(line, MARGIN_MM, y + 4);
      y += 6;
    }
  };

  const notes = content.notes?.trim();
  if (notes) {
    addHeading('Notes');
    addWrappedText(notes);
    y += 3;
  }

  if (content.measurements && content.measurements.length > 0) {
    addHeading('Current measurements');
    for (const measurement of content.measurements) {
      addWrappedText(`- ${measurement}`);
    }
  }

  if (!notes && (!content.measurements || content.measurements.length === 0)) {
    addWrappedText('No notes or current measurements were selected for this report.');
  }
}

/**
 * Adds a north arrow symbol to the PDF.
 */
function addNorthArrowToPDF(pdf: jsPDF, x: number, y: number): void {
  const size = 10;
  const centerX = x + size / 2;
  const centerY = y + size / 2;

  // Draw arrow pointing up
  pdf.setDrawColor(0, 0, 0);
  pdf.setFillColor(0, 0, 0);

  // Arrow body (line)
  pdf.setLineWidth(0.5);
  pdf.line(centerX, centerY + size / 2 - 1, centerX, centerY - size / 2 + 2);

  // Arrow head (triangle)
  const headSize = 3;
  pdf.triangle(
    centerX,
    centerY - size / 2,
    centerX - headSize / 2,
    centerY - size / 2 + headSize,
    centerX + headSize / 2,
    centerY - size / 2 + headSize,
    'F'
  );

  // "N" label
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'bold');
  pdf.text('N', centerX, centerY - size / 2 - 1, { align: 'center' });
}

/**
 * Adds a legend to the PDF showing layer names and their symbology.
 */
function addLegendToPDF(
  pdf: jsPDF,
  layers: Array<{ name: string; color: string; geometryType: string }>,
  x: number,
  y: number,
  maxWidth: number
): void {
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Legend', x, y + 4);

  let currentY = y + 10;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);

  for (const layer of layers) {
    // Draw symbol
    const color = hexToRgb(layer.color);
    pdf.setFillColor(color.r, color.g, color.b);
    pdf.setDrawColor(0, 0, 0);

    if (layer.geometryType === 'Point') {
      pdf.circle(x + 3, currentY, 2, 'FD');
    } else if (layer.geometryType === 'LineString') {
      pdf.setDrawColor(color.r, color.g, color.b);
      pdf.setLineWidth(1);
      pdf.line(x + 1, currentY, x + 6, currentY);
      pdf.setDrawColor(0, 0, 0);
    } else {
      pdf.rect(x + 1, currentY - 2, 5, 4, 'FD');
    }

    // Draw label
    const labelText = layer.name.length > 20 ? layer.name.substring(0, 17) + '...' : layer.name;
    pdf.text(labelText, x + 9, currentY + 1);
    currentY += 7;
  }
}

/**
 * Adds a scale bar to the PDF.
 */
function addScaleBarToPDF(
  pdf: jsPDF,
  map: Map,
  x: number,
  y: number,
  availableWidth: number
): void {
  const view = map.getView();
  const resolution = view.getResolution();
  if (!resolution) return;

  // Calculate scale: resolution is in meters/pixel for EPSG:3857
  // We want a nice round number for the scale bar
  const mapWidthMeters = resolution * (map.getSize()?.[0] ?? 800);
  const scaleBarWidthMM = Math.min(availableWidth * 0.4, 60);

  // Find a nice round distance
  const metersPerMM = mapWidthMeters / availableWidth;
  const rawDistance = metersPerMM * scaleBarWidthMM;
  const niceDistance = getNiceScaleDistance(rawDistance);
  const actualWidthMM = niceDistance / metersPerMM;

  // Draw scale bar
  pdf.setDrawColor(0, 0, 0);
  pdf.setFillColor(0, 0, 0);
  pdf.setLineWidth(0.3);

  // Main bar
  pdf.rect(x, y, actualWidthMM, 2, 'FD');

  // Alternating segments
  const segments = 4;
  const segWidth = actualWidthMM / segments;
  for (let i = 0; i < segments; i++) {
    if (i % 2 === 0) {
      pdf.setFillColor(0, 0, 0);
    } else {
      pdf.setFillColor(255, 255, 255);
    }
    pdf.rect(x + i * segWidth, y, segWidth, 2, 'FD');
  }

  // Labels
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'normal');
  pdf.text('0', x, y + 5);

  const label =
    niceDistance >= 1000
      ? `${(niceDistance / 1000).toFixed(0)} km`
      : `${niceDistance.toFixed(0)} m`;
  pdf.text(label, x + actualWidthMM, y + 5, { align: 'right' });
}

/**
 * Gets a nice round distance for the scale bar.
 */
function getNiceScaleDistance(rawDistance: number): number {
  const niceValues = [
    1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000, 50000, 100000, 200000,
    500000, 1000000,
  ];
  for (const val of niceValues) {
    if (val >= rawDistance * 0.5) {
      return val;
    }
  }
  return rawDistance;
}

// ---------------------------------------------------------------------------
// Image export
// ---------------------------------------------------------------------------

/**
 * Exports the map as an image (PNG, JPG, or SVG).
 *
 * @param map - The OpenLayers Map instance
 * @param options - Image export configuration
 * @returns A Blob containing the image
 *
 * **Validates: Requirements 37.2, 37.3, 37.4, 37.5, 37.6**
 */
export function exportMapImage(map: Map, options: ImageExportOptions): Blob {
  const mapCanvas = captureMapCanvas(map);

  // Create output canvas at requested dimensions
  const outputCanvas = document.createElement('canvas');
  outputCanvas.width = options.width;
  outputCanvas.height = options.height;
  const ctx = outputCanvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get 2D context for export canvas.');
  }

  // Handle transparency for PNG
  if (options.format === 'png' && options.preserveTransparency) {
    // Don't fill background - keep transparent
  } else {
    // Fill with white background for JPG
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, options.width, options.height);
  }

  // Scale and draw the map canvas to fit the output dimensions
  ctx.drawImage(mapCanvas, 0, 0, options.width, options.height);

  if (options.format === 'svg') {
    return exportAsSVG(outputCanvas, options.width, options.height);
  }

  // Convert to blob
  const mimeType = options.format === 'jpg' ? 'image/jpeg' : 'image/png';
  const quality = options.format === 'jpg' ? 0.92 : undefined;
  const dataUrl = outputCanvas.toDataURL(mimeType, quality);
  return dataURLToBlob(dataUrl);
}

/**
 * Exports canvas content as SVG by embedding the raster image.
 */
function exportAsSVG(canvas: HTMLCanvasElement, width: number, height: number): Blob {
  const dataUrl = canvas.toDataURL('image/png');
  const svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" 
     width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  <image width="${width}" height="${height}" xlink:href="${dataUrl}"/>
</svg>`;
  return new Blob([svgContent], { type: 'image/svg+xml' });
}

// ---------------------------------------------------------------------------
// Layout composer PDF generation
// ---------------------------------------------------------------------------

/**
 * Generates a PDF from a composed layout with multiple elements.
 *
 * @param map - The OpenLayers Map instance
 * @param layout - The composed layout with positioned elements
 * @param pageSize - Page size
 * @param orientation - Page orientation
 * @param dpi - Output DPI
 * @param layers - Layer info for legend
 * @returns A Blob containing the PDF
 *
 * **Validates: Requirement 38.5**
 */
export function generateLayoutPDF(
  map: Map,
  layout: MapLayout,
  pageSize: PageSize,
  orientation: Orientation,
  dpi: DPI,
  layers?: Array<{ name: string; color: string; geometryType: string }>
): Blob {
  const dims = getPageDimensions(pageSize, orientation);

  const pdf = new jsPDF({
    orientation: orientation === 'landscape' ? 'landscape' : 'portrait',
    unit: 'mm',
    format:
      pageSize === 'Custom'
        ? [dims.width, dims.height]
        : pageSize.toLowerCase() === 'letter'
          ? 'letter'
          : pageSize.toLowerCase(),
  });

  // Render map frame (aspect-fit inside frame)
  if (layout.mapFrame) {
    const mapCanvas = captureMapCanvas(map);
    const mapDataUrl = mapCanvas.toDataURL('image/png');
    const fit = fitImageInBox(
      mapCanvas.width,
      mapCanvas.height,
      layout.mapFrame.x,
      layout.mapFrame.y,
      layout.mapFrame.width,
      layout.mapFrame.height
    );
    pdf.setDrawColor(220, 220, 220);
    pdf.setFillColor(255, 255, 255);
    pdf.rect(
      layout.mapFrame.x,
      layout.mapFrame.y,
      layout.mapFrame.width,
      layout.mapFrame.height,
      'FD'
    );
    pdf.addImage(mapDataUrl, 'PNG', fit.x, fit.y, fit.w, fit.h, undefined, 'FAST');
  }

  // Render title
  if (layout.title && layout.title.content) {
    const fontSize = (layout.title.style?.fontSize as number) ?? 16;
    pdf.setFontSize(fontSize);
    pdf.setFont('helvetica', 'bold');
    pdf.text(
      layout.title.content,
      layout.title.x + layout.title.width / 2,
      layout.title.y + layout.title.height / 2,
      { align: 'center' }
    );
  }

  // Render legend
  if (layout.legend && layers && layers.length > 0) {
    addLegendToPDF(pdf, layers, layout.legend.x, layout.legend.y, layout.legend.width);
  }

  // Render scale bar
  if (layout.scaleBar) {
    addScaleBarToPDF(pdf, map, layout.scaleBar.x, layout.scaleBar.y, layout.scaleBar.width);
  }

  // Render north arrow
  if (layout.northArrow) {
    addNorthArrowToPDF(pdf, layout.northArrow.x, layout.northArrow.y);
  }

  // Render text boxes
  for (const textBox of layout.textBoxes) {
    if (textBox.content) {
      const fontSize = (textBox.style?.fontSize as number) ?? 10;
      pdf.setFontSize(fontSize);
      pdf.setFont('helvetica', 'normal');
      pdf.text(textBox.content, textBox.x, textBox.y + fontSize * 0.35);
    }
  }

  return pdf.output('blob');
}

// ---------------------------------------------------------------------------
// Utility functions
// ---------------------------------------------------------------------------

/**
 * Converts a hex color string to RGB values.
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 51, g: 136, b: 255 }; // Default blue
}

/**
 * Converts a data URL to a Blob.
 */
function dataURLToBlob(dataUrl: string): Blob {
  const parts = dataUrl.split(',');
  const mime = parts[0].match(/:(.*?);/)?.[1] ?? 'image/png';
  const byteString = atob(parts[1]);
  const arrayBuffer = new ArrayBuffer(byteString.length);
  const uint8Array = new Uint8Array(arrayBuffer);

  for (let i = 0; i < byteString.length; i++) {
    uint8Array[i] = byteString.charCodeAt(i);
  }

  return new Blob([arrayBuffer], { type: mime });
}

/**
 * Triggers a file download from a Blob.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
